import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, CheckCircle2, XCircle, Loader2, X, HelpCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fullImg = (p) => `${process.env.REACT_APP_BACKEND_URL}${p}`;

/** CameraTestGrid — suit un test caméra ×N et affiche la grille + l'indicateur de constance. */
export const CameraTestGrid = ({ projectId, testId, onClose }) => {
  const [data, setData] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!testId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/projects/${projectId}/generation/camera-test/${testId}`, { withCredentials: true });
        if (cancelled) return;
        setData(r.data);
        if (!r.data.all_done || r.data.items.some((i) => i.status === 'completed' && !i.camera_check)) {
          timer.current = setTimeout(poll, 6000);
        }
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Suivi du test caméra indisponible');
      }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer.current); };
  }, [projectId, testId]);

  if (!testId) return null;
  const items = data?.items || [];
  const tone = !data?.all_done ? 'var(--c-text-muted)' : data.consistent === data.completed && data.completed > 0 ? 'var(--c-success)' : data.consistent >= Math.ceil((data.completed || 1) * 0.6) ? '#FBBF24' : '#FCA5A5';

  return (
    <div className="mt-4 rounded-xl p-4" style={{ border: '1px solid rgba(217,160,91,0.35)', background: 'rgba(217,160,91,0.05)' }} data-testid="camera-test-grid">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4" style={{ color: 'var(--c-gold)' }} />
          <span className="text-[12px] font-semibold" style={{ color: 'var(--c-text)' }}>Test caméra ×{data?.total || items.length || '…'}</span>
          <span className="text-[11px]" style={{ color: 'var(--c-text-muted)' }}>
            {data?.vue_angle || 'Aérienne isométrique 45°'} · masque {data?.mask_projected === true ? 'projeté' : 'top-down'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[12px] font-semibold hud-tabular" style={{ color: tone }} data-testid="camera-test-consistency">
            {data?.all_done ? `Constance : ${data.consistent}/${data.completed} en losange iso` : `${data?.completed || 0}/${data?.total || items.length} rendus…`}
          </span>
          <button type="button" onClick={onClose} className="hud-btn hud-btn-ghost hud-btn-sm aspect-square px-0" data-testid="camera-test-close"><X className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {items.map((it, idx) => {
          const chk = it.camera_check;
          return (
            <div key={it.job_id} className="relative aspect-square rounded-lg overflow-hidden flex items-center justify-center" style={{ border: `1px solid ${chk?.ok ? 'rgba(52,211,153,0.6)' : chk && chk.ok === false ? 'rgba(252,165,165,0.6)' : 'var(--c-border)'}`, background: 'rgba(0,0,0,0.3)' }} data-testid={`camera-test-tile-${idx}`}>
              {it.image_url ? (
                <a href={fullImg(it.image_url)} target="_blank" rel="noreferrer" className="block w-full h-full">
                  <img src={fullImg(it.image_url)} alt={`Rendu ${idx + 1}`} className="w-full h-full object-cover" />
                </a>
              ) : it.status === 'failed' ? (
                <span className="text-[10px] px-2 text-center" style={{ color: '#FCA5A5' }}>Échec{it.error ? ` · ${String(it.error).slice(0, 40)}` : ''}</span>
              ) : (
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--c-text-muted)' }} />
              )}
              <span className="absolute top-1 left-1 text-[10px] px-1 rounded hud-tabular" style={{ background: 'rgba(0,0,0,0.6)', color: '#E5E7EB' }}>#{idx + 1}</span>
              {it.status === 'completed' && (
                <span className="absolute bottom-1 right-1 rounded-full p-0.5" style={{ background: 'rgba(0,0,0,0.65)' }} title={chk?.note || (chk ? '' : 'Analyse du cadrage…')}>
                  {!chk ? <Loader2 className="w-3.5 h-3.5 animate-spin text-white" /> : chk.ok ? <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#34D399' }} /> : chk.ok === false ? <XCircle className="w-3.5 h-3.5" style={{ color: '#FCA5A5' }} /> : <HelpCircle className="w-3.5 h-3.5 text-white" />}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CameraTestGrid;
